import React, { createContext, useContext, useMemo, useState } from 'react';
import { ThemeProvider } from '@mui/material';
import { Outlet } from 'react-router-dom';
import { DEFAULT_PAGE_SIZE } from '../../Tools/Table/pagination';
import { createInterventionCreationTheme } from '../Interventions/InterventionCreation/theme';
import { EMPTY_QUERY, FollowUpQuery } from './followUp';

export type FollowUpView = 'list' | 'map';

type FollowUpState = {
  query: FollowUpQuery;
  setQuery: (query: FollowUpQuery) => void;
  page: number;
  setPage: (page: number) => void;
  pageSize: number;
  setPageSize: (pageSize: number) => void;
  view: FollowUpView;
  setView: (view: FollowUpView) => void;
  selectedIds: number[];
  setSelectedIds: (ids: number[]) => void;
};

const FollowUpContext = createContext<FollowUpState | null>(null);

/**
 * Porte l'état de la liste au-dessus de la liste et de la fiche : le retour depuis une
 * fiche retrouve les filtres, la page et la vue tels qu'on les a laissés.
 *
 * Applique aussi le thème B-Connect du nouveau design (celui de la création
 * d'intervention) aux deux écrans du suivi.
 */
export default function FollowUpLayout() {
  const [query, setQueryState] = useState<FollowUpQuery>(EMPTY_QUERY);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSizeState] = useState(DEFAULT_PAGE_SIZE);
  const [view, setView] = useState<FollowUpView>('list');
  const [selectedIds, setSelectedIds] = useState<number[]>([]);

  const value = useMemo<FollowUpState>(
    () => ({
      query,
      // Un nouveau filtre ramène en première page et vide la sélection, qui ne
      // correspondrait plus à ce qui est affiché.
      setQuery: (next) => {
        setQueryState(next);
        setPage(1);
        setSelectedIds([]);
      },
      page,
      setPage,
      pageSize,
      setPageSize: (next) => {
        setPageSizeState(next);
        setPage(1);
      },
      view,
      setView,
      selectedIds,
      setSelectedIds
    }),
    [query, page, pageSize, view, selectedIds]
  );

  return (
    <ThemeProvider theme={createInterventionCreationTheme}>
      <FollowUpContext.Provider value={value}>
        <Outlet />
      </FollowUpContext.Provider>
    </ThemeProvider>
  );
}

export function useFollowUpState(): FollowUpState {
  const context = useContext(FollowUpContext);
  if (context === null) {
    throw new Error('useFollowUpState must be used under FollowUpLayout');
  }
  return context;
}
